/**
 * Video embedding utility functions
 */
import {
  VIDEO_BUTTON_ICONS,
  VIDEO_CONSTANTS,
  VideoAttributes,
  VideoConfiguration,
  VideoControlsAttributes,
  VideoTransformAttributes,
} from './video-embedding.types';

/**
 * Parse numeric attribute with fallback to default value
 */
export function parseNumericAttribute(value: string | null, defaultValue: number): number {
  if (!value) return defaultValue;

  const parsed = parseFloat(value);

  return isNaN(parsed) ? defaultValue : parsed;
}

/**
 * Extract video attributes from anchor element
 */
export function extractVideoAttributes(
  anchorElement: Element,
  imageElement: Element
): VideoAttributes | null {
  const id = anchorElement.getAttribute('id') || '';
  const sourceUrl = anchorElement.getAttribute('xlink:href') || '';
  const videoType = (anchorElement.getAttribute('data-video-type') ||
    VIDEO_CONSTANTS.DEFAULT_VIDEO_TYPE) as VideoAttributes['videoType'];

  const width = parseNumericAttribute(imageElement.getAttribute('width'), 0);
  const height = parseNumericAttribute(imageElement.getAttribute('height'), 0);
  const x = parseNumericAttribute(imageElement.getAttribute('x'), 0);
  const y = parseNumericAttribute(imageElement.getAttribute('y'), 0);

  // Validate essential attributes
  if (!id || !sourceUrl || width <= 0 || height <= 0) {
    console.warn('Skipping video embedding due to missing or invalid attributes:', {
      id,
      sourceUrl,
      width,
      height,
    });

    return null;
  }

  return { id, sourceUrl, width, height, x, y, videoType };
}

/**
 * Extract video controls coordinates from anchor element
 */
export function extractControlsAttributes(
  anchorElement: Element,
  imageX: number,
  imageY: number
): VideoControlsAttributes {
  const dataBoxCoords = anchorElement.getAttribute('data-box-coordinates');
  const coordinates = dataBoxCoords?.split(' ').map((coordinate) => parseFloat(coordinate)) || [];

  const [clipX = 0, clipY = 0, clipWidth = 100, clipHeight = 100] = coordinates;

  const x = Math.round(clipX - imageX);
  const y = Math.round(clipY - imageY);

  return { x, y, width: clipWidth, height: clipHeight };
}

/**
 * Extract transform attributes from element and its parents
 */
export function extractTransformAttributes(anchorElement: Element): VideoTransformAttributes {
  const transforms: string[] = [];

  // Add anchor transform if exists
  const anchorTransform = anchorElement.getAttribute('transform');

  if (anchorTransform) {
    transforms.push(anchorTransform);
  }

  // Collect transforms from parent elements up to SVG
  let parentElement = anchorElement.parentElement;

  while (parentElement && parentElement.tagName.toLowerCase() !== 'svg') {
    const parentTransform = parentElement.getAttribute('transform');

    if (parentTransform) {
      transforms.unshift(parentTransform); // Parent transforms applied first
    }

    parentElement = parentElement.parentElement;
  }

  const transform = transforms.join(' ');

  // Extract clip-path from anchor or parent g element
  let clipPath = anchorElement.getAttribute('clip-path') || '';

  if (!clipPath) {
    const parentG = anchorElement.closest('g[clip-path]');

    if (parentG) {
      clipPath = parentG.getAttribute('clip-path') || '';
    }
  }

  return { transform, clipPath };
}

/**
 * Parse video configuration from anchor element
 */
export function parseVideoConfiguration(element: Element): VideoConfiguration | null {
  const imageElement = element.querySelector('image');

  if (!imageElement) return null;

  const attributes = extractVideoAttributes(element, imageElement);

  if (!attributes) return null;

  const controls = extractControlsAttributes(element, attributes.x, attributes.y);
  const transforms = extractTransformAttributes(element);

  return { attributes, controls, transforms };
}

/**
 * Calculate scale factor based on page dimensions
 */
export function calculateScaleFactor(pageDimension: { width?: number; height?: number }): number {
  if (!pageDimension?.width || !pageDimension?.height) {
    return 1;
  }

  const smallerDimension = Math.min(pageDimension.width, pageDimension.height);

  return smallerDimension / VIDEO_CONSTANTS.REFERENCE_CANVAS_SIZE;
}

/**
 * Create a video button element
 */
export function createVideoButton(
  buttonType: 'play' | 'pause',
  videoId: string,
  scaleFactor: number
): HTMLButtonElement {
  const button = document.createElement('button');

  button.setAttribute('data-action', buttonType);
  button.setAttribute('data-video-id', videoId);
  button.setAttribute('data-video', videoId);
  button.style.width = `${VIDEO_CONSTANTS.DEFAULT_BUTTON_SIZE}px`;
  button.style.height = `${VIDEO_CONSTANTS.DEFAULT_BUTTON_SIZE}px`;
  button.style.transform = `scale(${scaleFactor})`;
  button.style.border = 'none';
  button.style.background = 'transparent';
  button.style.cursor = 'pointer';
  button.style.padding = '0';
  button.ariaLabel = `${buttonType} video`;
  button.innerHTML = VIDEO_BUTTON_ICONS[buttonType.toUpperCase() as 'PLAY' | 'PAUSE'];

  return button;
}

/**
 * Create video controls container with play/pause buttons
 */
export function createVideoControllers(
  videoId: string,
  controls: VideoControlsAttributes,
  scaleFactor: number
): HTMLDivElement {
  const controlsDiv = document.createElement('div');

  controlsDiv.setAttribute('data-video-controls', videoId);
  controlsDiv.style.position = 'absolute';
  controlsDiv.style.left = `${controls.x}px`;
  controlsDiv.style.top = `${controls.y}px`;
  controlsDiv.style.width = `${controls.width}px`;
  controlsDiv.style.height = `${controls.height}px`;
  controlsDiv.style.cursor = 'pointer';
  controlsDiv.style.display = 'flex';
  controlsDiv.style.alignItems = 'center';
  controlsDiv.style.justifyContent = 'center';
  controlsDiv.style.boxSizing = 'border-box';
  controlsDiv.style.pointerEvents = 'auto';

  const playButton = createVideoButton('play', videoId, scaleFactor);
  const pauseButton = createVideoButton('pause', videoId, scaleFactor);

  controlsDiv.appendChild(playButton);
  controlsDiv.appendChild(pauseButton);

  return controlsDiv;
}

/**
 * Store video configuration as data attributes on anchor element
 */
export function storeVideoDataAttributes(anchorElement: Element, config: VideoConfiguration): void {
  const { attributes, controls, transforms } = config;

  anchorElement.setAttribute('data-video-width', String(attributes.width));
  anchorElement.setAttribute('data-video-height', String(attributes.height));
  anchorElement.setAttribute('data-video-x', String(attributes.x));
  anchorElement.setAttribute('data-video-y', String(attributes.y));
  anchorElement.setAttribute('data-video-url', attributes.sourceUrl);
  anchorElement.setAttribute('data-video-type', attributes.videoType);
  anchorElement.setAttribute('data-video-controls-x', String(controls.x));
  anchorElement.setAttribute('data-video-controls-y', String(controls.y));
  anchorElement.setAttribute('data-video-controls-width', String(controls.width));

  anchorElement.setAttribute('data-video-controls-height', String(controls.height));

  if (transforms.transform) {
    anchorElement.setAttribute('data-video-transform', transforms.transform);
  }

  if (transforms.clipPath) {
    anchorElement.setAttribute('data-video-clip-path', transforms.clipPath);
  }
}

/**
 * Remove video link and mark as button
 */
export function prepareVideoAnchorElement(anchorElement: Element): void {
  if (anchorElement.hasAttribute('xlink:href')) {
    anchorElement.removeAttribute('xlink:href');
    anchorElement.setAttribute('role', 'button');
  }
}

/**
 * Read stored video configuration from data attributes
 */
export function readStoredVideoConfiguration(anchorElement: Element): VideoConfiguration | null {
  const id = anchorElement.getAttribute('id') || '';
  const sourceUrl = anchorElement.getAttribute('data-video-url') || '';
  const videoType = (anchorElement.getAttribute('data-video-type') ||
    VIDEO_CONSTANTS.DEFAULT_VIDEO_TYPE) as VideoAttributes['videoType'];

  const width = parseNumericAttribute(anchorElement.getAttribute('data-video-width'), 0);
  const height = parseNumericAttribute(anchorElement.getAttribute('data-video-height'), 0);
  const x = parseNumericAttribute(anchorElement.getAttribute('data-video-x'), 0);
  const y = parseNumericAttribute(anchorElement.getAttribute('data-video-y'), 0);

  const controlsX = parseNumericAttribute(anchorElement.getAttribute('data-video-controls-x'), 0);
  const controlsY = parseNumericAttribute(anchorElement.getAttribute('data-video-controls-y'), 0);
  const controlsWidth = parseNumericAttribute(
    anchorElement.getAttribute('data-video-controls-width'),
    0
  );
  const controlsHeight = parseNumericAttribute(
    anchorElement.getAttribute('data-video-controls-height'),
    0
  );

  // Skip if essential attributes are missing or invalid
  if (!id || !sourceUrl || width <= 0 || height <= 0) {
    console.warn('Skipping video creation due to missing or invalid attributes:', {
      id,
      sourceUrl,
      width,
      height,
    });

    return null;
  }

  const transform = anchorElement.getAttribute('data-video-transform') || '';
  const clipPath = anchorElement.getAttribute('data-video-clip-path') || '';

  return {
    attributes: { id, sourceUrl, width, height, x, y, videoType },
    controls: {
      x: controlsX,
      y: controlsY,
      width: controlsWidth,
      height: controlsHeight,
    },
    transforms: { transform, clipPath },
  };
}
